import {
  AvatarVariantAbilityContext,
  getAvatarVariantAbilityContext,
} from '@avatar/utils/avatar';
import logger from 'util/logger';
import { ActorVariant } from '../../../../../server/graphql/__generated.types';

export interface VariantWithVersion {
  variant?: ActorVariant;
  variantVersion?: string;
}

export const getAvatarSwapValidationContextFromAbilities = (
  fromAbility: AvatarVariantAbilityContext | null,
  toAbility: AvatarVariantAbilityContext | null,
  editor?: {
    hasCues?: boolean;
    hasReplacements?: boolean;
  }
): {
  canImmediatelySwap: boolean;
  lostFeatures: {
    cues: boolean;
    replacements: boolean;
  };
} => {
  if (!fromAbility || !toAbility) {
    logger.warn(`getAvatarSwapValidationContext: invalid swapping context`, {
      fromAbilityIsNull: fromAbility == null,
      toAbilityIsNull: toAbility == null,
    });
    // should never occur - but in the chance it does - avoid any validation
    return {
      canImmediatelySwap: true,
      lostFeatures: {
        cues: false,
        replacements: false,
      },
    };
  }
  const cuesDifferential =
    fromAbility?.supportsCues && !toAbility?.supportsCues;
  const replacementsDifferential =
    fromAbility?.supportsReplacements && !toAbility?.supportsReplacements;
  let losingCues = false;
  let losingReplacements = false;
  if (cuesDifferential) {
    losingCues = editor?.hasCues ?? false;
  }
  if (replacementsDifferential) {
    losingReplacements = editor?.hasReplacements ?? false;
  }
  if (!editor?.hasCues && !editor?.hasReplacements) {
    return {
      canImmediatelySwap: true,
      lostFeatures: {
        cues: losingCues,
        replacements: losingReplacements,
      },
    };
  }
  return {
    canImmediatelySwap: !losingCues && !losingReplacements,
    lostFeatures: {
      cues: losingCues,
      replacements: losingReplacements,
    },
  };
};

/**
 * @note this can be simplified by either using a cache lookup or
 * providing the semantic version of the tts model
 */
export const getAvatarSwapValidationContext = (
  fromAvatar: VariantWithVersion,
  toAvatar: VariantWithVersion,
  editor?: {
    hasCues?: boolean;
    hasReplacements?: boolean;
  }
) => {
  if (
    !fromAvatar.variant ||
    !toAvatar.variant ||
    !fromAvatar.variantVersion ||
    !toAvatar.variantVersion
  ) {
    logger.warn(`getAvatarSwapValidationContext: invalid swapping context`, {
      fromAvatar: fromAvatar.variant,
      fromAvatarVersion: fromAvatar.variantVersion,
      toAvatar: toAvatar.variant,
      toAvatarVersion: toAvatar.variantVersion,
    });
    // should never occur - but in the chance it does - avoid any validation
    return {
      canImmediatelySwap: true,
      lostFeatures: {
        cues: false,
        replacements: false,
      },
    };
  }
  const fromAbility = getAvatarVariantAbilityContext(
    fromAvatar.variant,
    fromAvatar.variantVersion
  );
  const toAbility = getAvatarVariantAbilityContext(
    toAvatar.variant,
    toAvatar.variantVersion
  );
  return getAvatarSwapValidationContextFromAbilities(
    fromAbility,
    toAbility,
    editor
  );
};
